import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { downloadFileFromUrl } from "@coworker/apprestructured/src/shared/helpers/downloadFileFromUrl";
import { formatFileSize } from "@coworker/apprestructured/src/shared/helpers/formatFileSize";
import { useTranslation } from "@coworker/locales";
import { useEffect, useMemo } from "react";
import { useTaskFiles } from "../../../services/useTaskFiles";
import { FileItem } from "../../Files/Files";
import { UseToastNotificationsResult } from "../../Notifications/hooks/useToastNotificationsState";

type UseFileUploadsArgs = {
  taskId: string | null;
  order: string[] | undefined;
  showToast: UseToastNotificationsResult["showToast"];
};

export const useFileUploads = ({
  taskId,
  order,
  showToast,
}: UseFileUploadsArgs) => {
  const { t } = useTranslation();
  const { data: fileUploads, isError: isFileUploadsError } = useTaskFiles(
    taskId,
    order
  );

  useEffect(() => {
    if (isFileUploadsError) {
      showToast({
        text: t("fileCouldNotBeLoadedOrDoesNotExistString"),
      });
    }
  }, [isFileUploadsError, showToast, t]);

  const fileItems = useMemo(() => {
    if (!fileUploads) {
      return [];
    }

    return fileUploads.map<FileItem>(({ file }) => ({
      name: file.name,
      size: formatFileSize(file.size),
      onDownload: async () => {
        try {
          await downloadFileFromUrl(file.url, file.name);
        } catch (e) {
          showToast({
            text: t("fileCouldNotBeLoadedOrDoesNotExistString"),
          });

          reportMessageToSentry(
            `Failed to download task file from upload '${file.id}'`,
            { error: e },
            "error"
          );
        }
      },
    }));
  }, [fileUploads, showToast, t]);

  return fileItems;
};
